import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  bootIntercom,
  shutdownIntercom,
  updateIntercom,
  getIntercomAppId,
  getIntercomIdentity,
} from "@/services/intercom";
import { useUserStore } from "@/store/useUserStore";

const useStableUserIdentity = (user) =>
  useMemo(() => getIntercomIdentity(user), [user]);

export const useIntercomMessenger = () => {
  const location = useLocation();
  const authUser = useSelector((state) => state.auth.user);
  const authToken = useSelector((state) => state.auth.token);
  const storedUser = useUserStore((state) => state.user);
  const storedToken = useUserStore((state) => state.token);
  const token = authToken || storedToken;
  const appId = getIntercomAppId();
  const identity = useStableUserIdentity(authUser || storedUser);
  const lastRouteRef = useRef("");
  const lastUpdateAtRef = useRef(0);

  useEffect(() => {
    if (!appId || !token || !identity) {
      shutdownIntercom();
      return;
    }

    bootIntercom({
      app_id: appId,
      ...identity,
    }).catch((error) => console.error("Failed to boot Intercom:", error));
    lastUpdateAtRef.current = Date.now();
  }, [appId, identity, token]);

  useEffect(() => {
    if (!appId || !token || !identity) return;

    const routeSignature = `${location.pathname}${location.search}`;
    if (lastRouteRef.current === routeSignature) return;

    const now = Date.now();
    if (now - lastUpdateAtRef.current < 90_000) {
      lastRouteRef.current = routeSignature;
      return;
    }

    lastRouteRef.current = routeSignature;
    lastUpdateAtRef.current = now;

    updateIntercom({
      app_id: appId,
      ...identity,
    });
  }, [appId, identity, location.pathname, location.search, token]);
};
